/*
 * Resume Studio — single source of truth for the live-preview HTML.
 *
 * IMPORTANT: This same function is used by:
 *   1. The ResumeStudio page (iframe srcDoc) for live preview
 *   2. The backend /export endpoint (fed via POST body) for DOCX/PDF
 *
 * Keeping both paths using identical HTML guarantees "what you see is what
 * you download" — which is our core differentiator vs competitors.
 *
 * The parser is the same proven parser used by ResumeDetail::buildFormattedHTML,
 * adapted to emit theme-variable-driven CSS instead of Word-inline styles.
 */

import { escapeHtml } from './htmlEscape';

// ---- Section recognition --------------------------------------------------

const KNOWN_SECTIONS = [
  'professional summary', 'summary', 'objective', 'profile', 'about',
  'experience', 'work experience', 'professional experience', 'employment history',
  'education', 'academic background',
  'skills', 'technical skills', 'core competencies', 'areas of expertise', 'key skills',
  'competencies', 'specialties', 'expertise', 'tools', 'technologies',
  'certifications', 'licenses & certifications', 'licenses and certifications',
  'projects', 'key projects', 'notable projects', 'side projects',
  'awards', 'honors', 'achievements', 'honors & awards',
  'volunteer', 'volunteer experience', 'community involvement',
  'languages', 'interests', 'hobbies', 'publications', 'references',
  'additional information', 'additional', 'leadership',
];

const SKILL_LIST_SECTIONS = new Set([
  'skills', 'technical skills', 'core competencies', 'areas of expertise', 'key skills',
  'competencies', 'specialties', 'expertise', 'tools', 'technologies',
  'certifications', 'licenses & certifications', 'licenses and certifications',
  'languages', 'interests', 'hobbies',
]);

const BULLET_RE = /^[-•*·▪►●◦▸]\s*/;

// Strip all leading bullet/marker chars (including bare `-` with no space) and
// return true if the item has any real content left. Guards against orphan
// "• -" bullets rendered from blank/stray list markers in the source.
const cleanListItem = (raw) => {
  let s = String(raw || '').trim();
  // Iteratively strip bullet chars at the start, in case of things like "- •"
  let prev;
  do {
    prev = s;
    s = s.replace(/^[-•*·▪►●◦▸]+\s*/, '').trim();
  } while (s !== prev);
  return s;
};

const isKnownSection = (s) => {
  const lower = s.toLowerCase().replace(/^#+\s*/, '').replace(/[:]+$/, '').trim();
  return KNOWN_SECTIONS.includes(lower);
};

const isContactLike = (line) => {
  if (!line) return false;
  if (/\S+@\S+\.\S+/.test(line)) return true;                               // email
  if (/\(?\d{3}\)?[\s\-.]?\d{3}[\s\-.]?\d{4}/.test(line)) return true;      // phone
  if (/(https?:\/\/|www\.|linkedin\.com|github\.com|\.com\/|\.io\/|\.dev)/i.test(line)) return true;
  if (line.length < 60 && /[A-Z][a-z]+,\s*[A-Z]{2}/.test(line)) return true; // City, ST
  return false;
};

// ---- Parser ---------------------------------------------------------------

export const parseResumeContent = (text) => {
  const lines = (text || '').split('\n');
  const sections = [];
  let currentSection = null;

  for (const rawLine of lines) {
    const line = rawLine.trim();

    // Markdown heading (# Title) or known section name or short ALL-CAPS line.
    // Blank lines are preserved as empty-string "items" so the renderer can
    // detect paragraph boundaries (runs of non-blank prose lines are later
    // merged into a single <p> to prevent mid-sentence pagination breaks).
    if (!line) {
      if (currentSection) currentSection.items.push('');
      else if (sections.length && sections[0].title === '__header__') {
        sections[0].items.push('');
      }
      continue;
    }

    const startsWithHash = /^#+\s+/.test(line);
    const isAllCaps = /^[A-Z][A-Z\s&/\-,]{2,38}$/.test(line) && line === line.toUpperCase();
    const isHeader = startsWithHash || isKnownSection(line) || isAllCaps;

    if (isHeader) {
      const cleanTitle = line.replace(/^#+\s*/, '').replace(/[:]+$/, '').trim();
      currentSection = { title: cleanTitle, items: [] };
      sections.push(currentSection);
    } else if (currentSection) {
      currentSection.items.push(line);
    } else {
      if (!sections.length || sections[0].title !== '__header__') {
        sections.unshift({ title: '__header__', items: [] });
      }
      sections[0].items.push(line);
    }
  }
  return sections;
};

// Merge consecutive "prose" lines (anything that is NOT a bullet, pipe/dash
// subheading, or pure date range) into single paragraphs, using blank lines as
// paragraph boundaries. This fixes the "performance." orphan bug where
// soft-wrapped source lines were each rendered as their own <p> and then got
// split across pages.
const DATE_LINE_RE = /^((\d{4}|Jan(uary)?|Feb(ruary)?|Mar(ch)?|Apr(il)?|May|Jun(e)?|Jul(y)?|Aug(ust)?|Sep(tember)?|Oct(ober)?|Nov(ember)?|Dec(ember)?)[^|]*?[–\-—](\s|$))/i;

const isStructuralLine = (s) => {
  if (!s) return false;
  if (BULLET_RE.test(s)) return true;
  const hasPipe = s.includes('|');
  const hasEmDash = s.includes(' — ') || s.includes(' – ');
  if ((hasPipe || hasEmDash) && s.length < 160) return true;
  if (DATE_LINE_RE.test(s) && s.length < 80 && !s.includes('|')) return true;
  return false;
};

const mergeSoftWrappedLines = (items) => {
  // Single pass that preserves contextual adjacency. Blank lines in `items`
  // are paragraph boundaries — they flush any buffer and break continuation.
  const out = [];
  let buf = [];            // current prose paragraph buffer
  let lastWasBullet = false;
  let lastHadBlankAfter = false;
  const flushBuf = () => {
    if (buf.length) {
      out.push(buf.join(' ').replace(/\s+/g, ' ').trim());
      buf = [];
    }
  };
  for (const it of items) {
    if (it === '') {
      flushBuf();
      lastHadBlankAfter = true;   // record paragraph boundary
      continue;
    }
    if (BULLET_RE.test(it)) {
      flushBuf();
      out.push(cleanListItem(it) ? `- ${cleanListItem(it)}` : '');
      lastWasBullet = true;
      lastHadBlankAfter = false;
      continue;
    }
    if (isStructuralLine(it)) {
      flushBuf();
      out.push(it);
      lastWasBullet = false;
      lastHadBlankAfter = false;
      continue;
    }
    // Plain prose line — is this a continuation of a preceding bullet?
    if (lastWasBullet && !lastHadBlankAfter) {
      // Append directly to the last bullet in `out`.
      out[out.length - 1] = `${out[out.length - 1]} ${it}`.replace(/\s+/g, ' ');
      continue;
    }
    buf.push(it);
    lastWasBullet = false;
    lastHadBlankAfter = false;
  }
  flushBuf();
  return out.filter(Boolean);
};

// ---- Renderer -------------------------------------------------------------

const renderHeader = (items, editable) => {
  if (!items || !items.length) return '';
  const name = items[0];
  const contactLines = [];
  const bodyLines = [];
  for (let k = 1; k < items.length; k++) {
    const ln = items[k];
    if (!ln) continue; // skip blank lines in header context — they're not content
    if (isContactLike(ln) && bodyLines.length === 0) contactLines.push(ln);
    else bodyLines.push(ln);
  }

  const editAttr = editable ? ' contenteditable="true"' : '';

  let html = `<h1 class="name" data-field="name"${editAttr}>${escapeHtml(name || 'Your Name')}</h1>`;
  if (contactLines.length) {
    // Flatten "a | b | c" into individual spans for nice separator styling.
    const flat = contactLines.join(' | ').split('|').map((s) => s.trim()).filter(Boolean);
    const contactHtml = flat
      .map((c) => `<span>${escapeHtml(c)}</span>`)
      .join('<span class="sep"> | </span>');
    html += `<div class="contact" data-field="contact"${editAttr}>${contactHtml}</div>`;
  }
  html += '<div class="divider"></div>';
  if (bodyLines.length) {
    // When the resume has no explicit section headers (common in AI-generated
    // or copy-pasted content), render the remaining lines through the full
    // section renderer under an implicit "Experience" title. This gives us
    // bullet coalescing, role subheadings, etc. — not just a paragraph dump.
    const looksStructured = bodyLines.some(
      (l) => BULLET_RE.test(l) || (l.includes('|') && l.length < 160) || / [—–] /.test(l),
    );
    if (looksStructured) {
      html += renderSection({ title: 'Experience', items: bodyLines }, editable);
    } else {
      // Merge soft-wrapped prose lines into single paragraphs (orphan-fix).
      const paras = mergeSoftWrappedLines(bodyLines);
      html += '<section class="section"><h2>Summary</h2>';
      for (const s of paras) html += `<p${editAttr}>${escapeHtml(s)}</p>`;
      html += '</section>';
    }
  }
  return html;
};

const renderSection = (section, editable) => {
  if (!section.items.length) return '';
  const title = section.title.toUpperCase();
  const key = section.title.toLowerCase();
  const editAttr = editable ? ' contenteditable="true"' : '';
  let html = `<section class="section"><h2>${escapeHtml(title)}</h2>`;

  if (SKILL_LIST_SECTIONS.has(key)) {
    // Render every non-empty item as a bullet — works whether user prefixed with "-" or not.
    const items = section.items
      .map(cleanListItem)
      .filter(Boolean);
    if (items.length) {
      html += '<ul>';
      for (const it of items) {
        html += `<li${editAttr}>${escapeHtml(it)}</li>`;
      }
      html += '</ul>';
    }
    html += '</section>';
    return html;
  }

  // Merge soft-wrapped prose lines BEFORE running bullet/subheading detection.
  // This prevents mid-paragraph pagination breaks (orphan word problem).
  const items = mergeSoftWrappedLines(section.items);

  // Experience-style: detect bullets / subheadings / paragraphs.
  // We group each "job block" (subheading + date-line + bullets + paragraphs)
  // into a wrapper div with page-break-inside:avoid so role/company stays with
  // its bullets — same technique used by Resume.io / Zety / Novoresume.
  let i = 0;
  let inJobBlock = false;
  const openJobBlock = () => {
    if (!inJobBlock) { html += '<div class="job-block">'; inJobBlock = true; }
  };
  const closeJobBlock = () => {
    if (inJobBlock) { html += '</div>'; inJobBlock = false; }
  };

  while (i < items.length) {
    const item = items[i];
    if (BULLET_RE.test(item)) {
      const group = [];
      while (i < items.length && BULLET_RE.test(items[i])) {
        const b = cleanListItem(items[i]);
        if (b) group.push(b);
        i++;
      }
      if (group.length) {
        html += '<ul>';
        for (const b of group) html += `<li${editAttr}>${escapeHtml(b)}</li>`;
        html += '</ul>';
      }
      continue;
    }

    if (DATE_LINE_RE.test(item) && item.length < 80 && !item.includes('|')) {
      html += `<p class="date-line"${editAttr}>${escapeHtml(item)}</p>`;
      i++;
      continue;
    }

    const hasPipe = item.includes('|');
    const hasEmDash = item.includes(' — ') || item.includes(' – ');
    const isSubheading = (hasPipe || hasEmDash) && item.length < 160;

    if (isSubheading) {
      // Starting a new role → close previous job block and open a fresh one.
      closeJobBlock();
      openJobBlock();
      const sep = hasPipe ? '|' : (item.includes(' — ') ? ' — ' : ' – ');
      const parts = item.split(sep).map((p) => p.trim()).filter(Boolean);
      const first = parts[0];
      const rest = parts.slice(1).join(' \u00B7 ');
      html += `<p class="role"${editAttr}><strong>${escapeHtml(first)}</strong>`;
      if (rest) html += `<span class="role-meta"> \u00B7 ${escapeHtml(rest)}</span>`;
      html += '</p>';
    } else {
      html += `<p${editAttr}>${escapeHtml(item)}</p>`;
    }
    i++;
  }
  closeJobBlock();

  html += '</section>';
  return html;
};

// ---- Final document builder ----------------------------------------------

export const buildStyledHTML = ({ content, cssVars, editable = false, pageSize = 'letter' }) => {
  const sections = parseResumeContent(content || '');
  const headerSection = sections.find((s) => s.title === '__header__');
  const bodySections = sections.filter((s) => s.title !== '__header__');

  const headerHtml = headerSection ? renderHeader(headerSection.items, editable) : '';
  const bodyHtml = bodySections.map((s) => renderSection(s, editable)).join('');
  const paperDim = pageSize === 'a4' ? { w: '210mm', h: '297mm' } : { w: '8.5in', h: '11in' };

  return `<!doctype html><html><head><meta charset="utf-8">
<link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Source+Sans+Pro:wght@400;600;700&family=IBM+Plex+Sans:wght@400;500;600;700&family=Playfair+Display:wght@600;700;800&family=EB+Garamond:wght@400;600;700&display=swap" rel="stylesheet">
<style>
  ${cssVars || ''}
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; background: #e5e7eb; }
  body { font-family: var(--body-font); padding: 32px 0; }
  .paper {
    width: ${paperDim.w}; min-height: ${paperDim.h};
    background: white; margin: 0 auto;
    box-shadow: 0 20px 40px rgba(0,0,0,0.12);
    padding: var(--margin);
    color: #1a1a1a;
    font-size: var(--body-size);
    line-height: var(--line-height);
  }
  .name {
    font-family: var(--heading-font);
    font-size: var(--name-size);
    font-weight: var(--name-weight);
    color: var(--accent);
    letter-spacing: var(--name-tracking);
    margin: 0 0 4px 0;
    text-align: var(--header-align);
    outline: none;
  }
  .contact {
    font-size: 9.5pt; color: #4b5563;
    text-align: var(--header-align);
    margin-bottom: var(--header-gap);
    outline: none;
    line-height: 1.4;
  }
  .contact .sep { color: #9ca3af; padding: 0 4px; }
  .divider { border-bottom: var(--divider); margin: 10px 0 14px 0; }
  .section { margin-bottom: 4px; }
  /* Pagination discipline — prevents orphan words and mid-section page breaks.
     Same technique used by Resume.io, Zety, Novoresume. Supports both CSS2
     (page-break-*, honored by Chromium + htmldocx) and CSS3 (break-*). */
  p, li { orphans: 3; widows: 3; }
  h2 {
    page-break-after: avoid;
    break-after: avoid-page;
    page-break-inside: avoid;
    break-inside: avoid;
  }
  .job-block {
    page-break-inside: avoid;
    break-inside: avoid;
  }
  .role, .date-line {
    page-break-after: avoid;
    break-after: avoid-page;
  }
  h2 {
    font-family: var(--heading-font);
    font-size: var(--h2-size);
    font-weight: var(--h2-weight);
    color: var(--accent);
    letter-spacing: var(--h2-tracking);
    text-transform: var(--h2-case);
    margin: 14px 0 6px 0;
    padding-bottom: var(--h2-pad);
    border-bottom: var(--h2-border);
    outline: none;
  }
  p { margin: 3px 0 5px 0; outline: none; }
  p.role { margin-top: 10px; margin-bottom: 1px; color: #0f1115; }
  p.role strong { font-weight: 700; }
  .role-meta { color: #6b7280; font-weight: 400; }
  p.date-line { margin: 0 0 4px 0; color: #6b7280; font-size: 0.92em; font-style: var(--meta-style); }
  ul { margin: 3px 0 8px 0; padding-left: 18px; }
  li { margin-bottom: 2px; outline: none; }
  [contenteditable="true"]:hover { background: #eff6ff; cursor: text; }
  [contenteditable="true"]:focus { background: #dbeafe; outline: 1px solid #002fa7; }
  @media print {
    html, body { background: white; }
    body { padding: 0; }
    .paper { box-shadow: none; margin: 0; }
  }
</style>
</head><body>
<article class="paper">
  ${headerHtml}
  ${bodyHtml}
</article>
</body></html>`;
};
